

import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  SimpleGrid,
  useToast,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getCustomerById, updateCustomer } from "../hooks/customers";
import { CustomerEdit } from "../utils/types";

const EditCustomerPage = () => {
//   const [form, setForm] = useState<Partial<Customer>>({});
//   const [customer, setCustomer] = useState<Customer | null>(null);
  const [customer, setCustomer] = useState<Omit<CustomerEdit, 'customer_notes' | 'interests' | 'lead'> | null>(null);
  const [form, setForm] = useState<Partial<Omit<CustomerEdit, 'customer_notes' | 'interests' | 'lead'>>>({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { orgId, customerId } = useParams<{ orgId: string; customerId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  // 🟦 Fetch customer data
  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        if (orgId && customerId) {
          const response = await getCustomerById(orgId, customerId);
          if (response.success && response.data) {
            const { notes, interests, lead, ...safeCustomer } = response.data;
            setCustomer(safeCustomer);
            setForm(response.data);
          } else {
            setError(response.data || "Failed to load customer");
          }
        }
      } catch (err) {
        setError("Something went wrong while fetching customer data.");
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [orgId, customerId]);

  // 🟩 Handle field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // 🟧 Detect changed fields
  const getChangedFields = () => {
    if (!customer) return {};
    const changed: Partial<CustomerEdit> = {};
    (Object.keys(form) as (keyof CustomerEdit)[]).forEach((k) => {
      const newValue = (form as any)[k];
      if (newValue !== customer[k]) {
        changed[k] = newValue as CustomerEdit[typeof k];
      }
    });
    return changed;
  };

  // 🟨 Submit changes
  const handleSubmit = async () => {
    if (!customer || !orgId || !customerId) return;

    const changedFields = getChangedFields();
    if (Object.keys(changedFields).length === 0) {
      toast({
        title: "No changes detected.",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    try {
      setSaving(true);
      console.log("Updated data fields", changedFields);
      const response = await updateCustomer(orgId, customerId, changedFields);

      if (response.success) {
        toast({
          title: "Customer updated successfully.",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        navigate(`../organization/${orgId}/customers/${customerId}`);
      } else {
        toast({
          title: "Update failed.",
          description: response.data || "Something went wrong.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: "Error updating customer.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner size="xl" m={8} />;
  if (error) return <Text color="red.500">{error}</Text>;
  if (!customer) return <Text>No customer found.</Text>;

  return (
    <Box p={8} maxW="4xl">
      <Heading mb={6}>Edit Customer</Heading>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel>First Name</FormLabel>
          <Input name="first_name" value={form.first_name || ""} onChange={handleChange} />
        </FormControl>

        <FormControl>
          <FormLabel>Last Name</FormLabel>
          <Input name="last_name" value={form.second_name || ""} onChange={handleChange} />
        </FormControl>

        <FormControl>
          <FormLabel>Email</FormLabel>
          <Input name="email" type="email" value={form.email || ""} onChange={handleChange} />
        </FormControl>

        <FormControl>
          <FormLabel>Phone</FormLabel>
          <Input name="phone" value={form.phone || ""} onChange={handleChange} />
        </FormControl>

        <FormControl>
          <FormLabel>Company Name</FormLabel>
          <Input name="company_name" value={form.company_name || ""} onChange={handleChange} />
        </FormControl>

        
        <FormControl>
          <FormLabel>Address</FormLabel>
          <Input name="address" value={form.address || ""} onChange={handleChange} />
        </FormControl>

        <FormControl>
          <FormLabel>City</FormLabel>
          <Input name="city" value={form.city || ""} onChange={handleChange} />
        </FormControl>

        <FormControl>
          <FormLabel>Country</FormLabel>
          <Input name="country" value={form.country || ""} onChange={handleChange} />
        </FormControl>
      </SimpleGrid>

      <Button mt={6} colorScheme="blue" isLoading={saving} onClick={handleSubmit}>
        Save Changes
      </Button>
    </Box>
  );
};

export default EditCustomerPage;
